import React, { useState, useEffect } from "react";
import { Divider, Typography, Spin, Alert } from "antd";
import CommonHeader from "../components/common/CommonHeader";
import CommonFooter from "../components/common/CommonFooter";
import ListingsFilter from "../components/common/FiltersBar";
import ListedPropertiesSection from "../components/listings/ListedPropertiesSection";
import { firebaseService } from "../lib/services/firebaseService";
import { Property } from "../types/propert";
import { Button } from "antd";

const { Title, Paragraph } = Typography;

const ListedPropertiesPage = () => {
  const [filters, setFilters] = useState({
    country: "All",
    city: "All",
    type: "All",
    value: "All",
  });

  const [adminProperties, setAdminProperties] = useState([]);
  const [userProperties, setUserProperties] = useState([]);
  const [filteredAdminProperties, setFilteredAdminProperties] = useState([]);
  const [filteredUserProperties, setFilteredUserProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let adminUnsubscribe;
    let userUnsubscribe;

    const initializeData = async () => {
      try {
        setLoading(true);

        // Subscribe to admin listed properties (public properties)
        adminUnsubscribe = firebaseService.getAdminListedProperties(
          (propertiesData) => {
            try {
              const transformedProperties = propertiesData.map(
                (propertyData) => new Property(propertyData)
              );
              setAdminProperties(transformedProperties);
              setFilteredAdminProperties(transformedProperties);
            } catch (err) {
              console.error("Error transforming admin properties:", err);
              setError("Error processing admin property data");
            }
          }
        );

        // Subscribe to user approved properties (limited properties)
        userUnsubscribe = firebaseService.getLimitedProperties(
          (propertiesData) => {
            try {
              const transformedProperties = propertiesData.map(
                (propertyData) => new Property(propertyData)
              );
              setUserProperties(transformedProperties);
              setFilteredUserProperties(transformedProperties);
            } catch (err) {
              console.error("Error transforming user properties:", err);
              setError("Error processing user property data");
            }
          }
        );

        setError(null);
      } catch (err) {
        console.error("Error initializing data:", err);
        setError("Failed to load properties");
      } finally {
        setLoading(false);
      }
    };

    initializeData();

    // Cleanup subscriptions on unmount
    return () => {
      if (adminUnsubscribe) adminUnsubscribe();
      if (userUnsubscribe) userUnsubscribe();
    };
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    applyFilters();
  };

  const applyFilters = () => {
    // Apply filters to admin properties
    let filteredAdmin = [...adminProperties];
    let filteredUser = [...userProperties];

    // Filter by country - only if not "All"
    if (filters.country !== "All") {
      filteredAdmin = filteredAdmin.filter((property) =>
        property.country?.toLowerCase().includes(filters.country.toLowerCase())
      );
      filteredUser = filteredUser.filter((property) =>
        property.country?.toLowerCase().includes(filters.country.toLowerCase())
      );
    }

    // Filter by city - only if not "All"
    if (filters.city !== "All") {
      filteredAdmin = filteredAdmin.filter((property) =>
        property.city?.toLowerCase().includes(filters.city.toLowerCase())
      );
      filteredUser = filteredUser.filter((property) =>
        property.city?.toLowerCase().includes(filters.city.toLowerCase())
      );
    }

    // Filter by property type - only if not "All"
    if (filters.type !== "All") {
      filteredAdmin = filteredAdmin.filter((property) =>
        property.propertyType
          ?.toLowerCase()
          .includes(filters.type.toLowerCase())
      );
      filteredUser = filteredUser.filter((property) =>
        property.propertyType
          ?.toLowerCase()
          .includes(filters.type.toLowerCase())
      );
    }

    // Filter by price range - only if not "All"
    if (filters.value !== "All") {
      filteredAdmin = filteredAdmin.filter((property) => {
        const price = property.finalPrice || property.listPrice || 0;
        return applyPriceFilter(price, filters.value);
      });
      filteredUser = filteredUser.filter((property) => {
        const price = property.finalPrice || property.listPrice || 0;
        return applyPriceFilter(price, filters.value);
      });
    }

    setFilteredAdminProperties(filteredAdmin);
    setFilteredUserProperties(filteredUser);
  };

  const applyPriceFilter = (price, filterValue) => {
    switch (filterValue) {
      case "1-10M":
        return price >= 1000000 && price <= 10000000;
      case "10-50M":
        return price >= 10000000 && price <= 50000000;
      case "50M+":
        return price >= 50000000;
      default:
        return true;
    }
  };

  const handleClearFilters = () => {
    setFilters({
      country: "All",
      city: "All",
      type: "All",
      value: "All",
    });
    setFilteredAdminProperties(adminProperties);
    setFilteredUserProperties(userProperties);
  };

  // Show all properties count when no filters are applied
  const isAnyFilterActive = Object.values(filters).some(
    (value) => value !== "All"
  );

  const totalProperties = adminProperties.length + userProperties.length;
  const totalFilteredProperties =
    filteredAdminProperties.length + filteredUserProperties.length;

  if (loading) {
    return (
      <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
        <CommonHeader bgColor="#DDC7BB" />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Spin size="large" />
        </div>
        <CommonFooter />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
        <CommonHeader bgColor="#DDC7BB" />
        <div style={{ padding: "20px" }}>
          <Alert message="Error" description={error} type="error" showIcon />
        </div>
        <CommonFooter />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      <CommonHeader bgColor="#DDC7BB" />

      <ListingsFilter
        filters={filters}
        handleChange={handleFilterChange}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
        isAnyFilterActive={isAnyFilterActive}
      />

      {/* Admin Listed Properties (Public) */}
      <ListedPropertiesSection
        title="PUBLIC LISTED PROPERTIES"
        description="These properties are listed by our admin team with full details approved for public release."
        listings={filteredAdminProperties}
        emptyMessage="No public properties available at the moment."
      />

      <Divider />

      {/* User Listed Properties (Limited) */}
      <ListedPropertiesSection
        title="LIMITED LISTED PROPERTIES"
        description="The following properties are listed by users. For more details, please express your interest, as the owners prefer not to disclose full information publicly."
        listings={filteredUserProperties}
        emptyMessage="No limited disclosure properties available at the moment."
      />

      {/* Sign Up Section */}
      <div
        style={{
          textAlign: "center",
          margin: "60px 0",
          padding: "40px 20px",
        }}
      >
        <Title level={3} style={{ fontWeight: "bold", marginBottom: 20 }}>
          LIST YOUR PROPERTY WITH US
        </Title>

        <Button
          type="primary"
          onClick={() => (window.location.href = "/list-property")}
          style={{
            backgroundColor: "#2b1b14",
            color: "#fff",
            fontWeight: 600,
            borderRadius: 6,
            padding: "0 40px",
            height: 45,
            width: "280px",
            border: "none",
            cursor: "pointer",
          }}
        >
          Lets Go →
        </Button>
      </div>

      <CommonFooter />
    </div>
  );
};

export default ListedPropertiesPage;
